import { useState, useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  Copy,
  Check,
  ExternalLink,
  BarChart3,
  QrCode,
  Settings,
  Loader2,
  Lock,
  Tag,
  Smartphone,
  Share2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getLinkAnalyticsFn, updateLinkFn } from "@/server/functions/links";
import { QRCodeGenerator } from "./qr-code-generator";
import {
  ClicksByDateChart,
  DeviceChart,
  BrowserChart,
  RefererList,
  StatsCard,
} from "./analytics-charts";
import type { Links } from "@/server/lib/appwrite.types";

const SHORT_DOMAIN = "l.zugl.in";

interface LinkDetailModalProps {
  link: Links | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

export function LinkDetailModal({
  link,
  open,
  onOpenChange,
  onUpdate,
}: LinkDetailModalProps) {
  const [copied, setCopied] = useState(false);
  const [analytics, setAnalytics] = useState<{
    totalClicks: number;
    clicksByDate: Record<string, number>;
    clicksByDevice: Record<string, number>;
    clicksByBrowser: Record<string, number>;
    clicksByOS: Record<string, number>;
    clicksByReferer: Record<string, number>;
  } | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit form state
  const [title, setTitle] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [password, setPassword] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [androidRedirect, setAndroidRedirect] = useState("");
  const [iosRedirect, setIosRedirect] = useState("");

  const getAnalytics = useServerFn(getLinkAnalyticsFn);
  const updateLink = useServerFn(updateLinkFn);

  useEffect(() => {
    if (link && open) {
      // Reset form state
      setTitle(link.title || "");
      setIsActive(link.isActive);
      setPassword(link.password || "");
      setUtmSource(link.utmSource || "");
      setUtmMedium(link.utmMedium || "");
      setUtmCampaign(link.utmCampaign || "");
      setAndroidRedirect(link.androidRedirect || "");
      setIosRedirect(link.iosRedirect || "");

      // Load analytics
      void loadAnalytics();
    }
  }, [link, open]);

  const loadAnalytics = async () => {
    if (!link) return;

    setIsLoadingAnalytics(true);
    try {
      const result = await getAnalytics({
        data: { linkId: link.$id, days: 30 },
      });
      setAnalytics(result);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const handleCopy = async () => {
    if (!link) return;

    const url = `https://${SHORT_DOMAIN}/${link.shortCode}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleSave = async () => {
    if (!link) return;

    setIsSaving(true);
    try {
      await updateLink({
        data: {
          id: link.$id,
          title: title.trim() || undefined,
          isActive,
          password: password.trim() || null,
          utmSource: utmSource.trim() || null,
          utmMedium: utmMedium.trim() || null,
          utmCampaign: utmCampaign.trim() || null,
          androidRedirect: androidRedirect.trim() || null,
          iosRedirect: iosRedirect.trim() || null,
        },
      });
      toast.success("Link updated!");
      onUpdate();
    } catch {
      toast.error("Failed to update link");
    } finally {
      setIsSaving(false);
    }
  };

  if (!link) return null;

  const shortUrl = `https://${SHORT_DOMAIN}/${link.shortCode}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1A1F2C] border-[#9B87F5]/20 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-mono flex items-center gap-2">
            <pre className="text-[#9B87F5] text-xs">
              {`┌──┐
│ L│
└──┘`}
            </pre>
            LINK_DETAILS
          </DialogTitle>
        </DialogHeader>

        {/* Link info header */}
        <div className="p-4 bg-[#0a0a0a] rounded-lg border border-[#9B87F5]/10 mb-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <a
                href={shortUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9B87F5] font-mono text-lg hover:text-white transition-colors flex items-center gap-2"
              >
                {shortUrl}
                <ExternalLink className="w-4 h-4" />
              </a>
              {link.title && (
                <p className="text-[#8E9196] font-mono text-sm mt-1">
                  {link.title}
                </p>
              )}
            </div>
            <Button
              onClick={handleCopy}
              className={`font-mono ${
                copied
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-[#9B87F5] hover:bg-[#8B5CF6]"
              } text-black`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  COPIED
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  COPY
                </>
              )}
            </Button>
          </div>
          <p className="text-[#8E9196] font-mono text-xs mt-2 truncate">
            → {link.originalUrl}
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="bg-[#0a0a0a] border border-[#9B87F5]/10 w-full">
            <TabsTrigger
              value="analytics"
              className="font-mono data-[state=active]:bg-[#9B87F5] data-[state=active]:text-black flex-1"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              ANALYTICS
            </TabsTrigger>
            <TabsTrigger
              value="qr"
              className="font-mono data-[state=active]:bg-[#9B87F5] data-[state=active]:text-black flex-1"
            >
              <QrCode className="w-4 h-4 mr-2" />
              QR_CODE
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="font-mono data-[state=active]:bg-[#9B87F5] data-[state=active]:text-black flex-1"
            >
              <Settings className="w-4 h-4 mr-2" />
              SETTINGS
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-4">
            {isLoadingAnalytics ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#9B87F5] animate-spin" />
              </div>
            ) : analytics ? (
              <div className="space-y-6">
                {/* Stats row */}
                <div className="grid grid-cols-2 gap-4">
                  <StatsCard
                    title="TOTAL_CLICKS"
                    value={link.clicks}
                    ascii={`┌───┐
│ ◉ │
└───┘`}
                  />
                  <StatsCard
                    title="LAST_30_DAYS"
                    value={analytics.totalClicks}
                    ascii={`┌───┐
│ ▓ │
└───┘`}
                  />
                </div>

                {/* Clicks over time */}
                <div className="p-4 bg-[#0a0a0a] rounded-lg border border-[#9B87F5]/10">
                  <h3 className="text-white font-mono text-sm mb-4">
                    CLICKS_OVER_TIME
                  </h3>
                  <ClicksByDateChart data={analytics.clicksByDate} />
                </div>

                {/* Device & Browser */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0a0a0a] rounded-lg border border-[#9B87F5]/10">
                    <h3 className="text-white font-mono text-sm mb-4">
                      DEVICES
                    </h3>
                    <DeviceChart data={analytics.clicksByDevice} />
                  </div>
                  <div className="p-4 bg-[#0a0a0a] rounded-lg border border-[#9B87F5]/10">
                    <h3 className="text-white font-mono text-sm mb-4">
                      BROWSERS
                    </h3>
                    <BrowserChart data={analytics.clicksByBrowser} />
                  </div>
                </div>

                {/* Referrers */}
                <div className="p-4 bg-[#0a0a0a] rounded-lg border border-[#9B87F5]/10">
                  <h3 className="text-white font-mono text-sm mb-4">
                    TOP_REFERRERS
                  </h3>
                  <RefererList data={analytics.clicksByReferer} />
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-[#8E9196] font-mono">
                FAILED_TO_LOAD_ANALYTICS
              </div>
            )}
          </TabsContent>

          {/* QR Code Tab */}
          <TabsContent value="qr" className="mt-4">
            <div className="flex flex-col items-center py-8">
              <pre className="font-mono text-[#9B87F5]/50 text-xs mb-4">
                {`┌─────────────────────┐
│   QR_CODE_GENERATOR │
└─────────────────────┘`}
              </pre>
              <QRCodeGenerator url={shortUrl} size={250} />
              <p className="text-[#8E9196] font-mono text-xs mt-4 text-center">
                Scan this code to open: {shortUrl}
              </p>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-4">
            <div className="space-y-4">
              {/* Active toggle */}
              <div className="flex items-center justify-between p-4 bg-[#0a0a0a] rounded-lg border border-[#9B87F5]/10">
                <div>
                  <p className="text-white font-mono text-sm">LINK_ACTIVE</p>
                  <p className="text-[#8E9196] font-mono text-xs">
                    Enable or disable this link
                  </p>
                </div>
                <Switch
                  checked={isActive}
                  onCheckedChange={setIsActive}
                  className="data-[state=checked]:bg-[#9B87F5]"
                />
              </div>

              {/* Title */}
              <div>
                <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                  <Tag className="w-4 h-4 text-[#9B87F5]" />
                  <span>title:</span>
                </div>
                <Input
                  type="text"
                  placeholder="Link title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono"
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                  <Lock className="w-4 h-4 text-[#9B87F5]" />
                  <span>password:</span>
                </div>
                <Input
                  type="password"
                  placeholder="Leave empty to remove password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono"
                />
              </div>

              {/* UTM Parameters */}
              <div>
                <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                  <Share2 className="w-4 h-4 text-[#9B87F5]" />
                  <span>utm_parameters:</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input
                    type="text"
                    placeholder="utm_source"
                    value={utmSource}
                    onChange={(e) => setUtmSource(e.target.value)}
                    className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono text-sm"
                  />
                  <Input
                    type="text"
                    placeholder="utm_medium"
                    value={utmMedium}
                    onChange={(e) => setUtmMedium(e.target.value)}
                    className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono text-sm"
                  />
                  <Input
                    type="text"
                    placeholder="utm_campaign"
                    value={utmCampaign}
                    onChange={(e) => setUtmCampaign(e.target.value)}
                    className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono text-sm"
                  />
                </div>
              </div>

              {/* Device redirects */}
              <div>
                <div className="flex items-center gap-2 text-[#8E9196] font-mono text-sm mb-2">
                  <Smartphone className="w-4 h-4 text-[#9B87F5]" />
                  <span>device_redirects:</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input
                    type="text"
                    placeholder="Android URL"
                    value={androidRedirect}
                    onChange={(e) => setAndroidRedirect(e.target.value)}
                    className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono text-sm"
                  />
                  <Input
                    type="text"
                    placeholder="iOS URL"
                    value={iosRedirect}
                    onChange={(e) => setIosRedirect(e.target.value)}
                    className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono text-sm"
                  />
                </div>
              </div>

              {/* Save button */}
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full bg-[#9B87F5] hover:bg-[#8B5CF6] text-black font-mono font-bold"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    SAVING...
                  </>
                ) : (
                  "[ SAVE CHANGES ]"
                )}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
